import { useEffect } from 'react';

/**
 * Keyboard shortcuts for the actions a counter presses all day.
 *
 * <p>
 * The companion to `enterKeyNavigation`: that one gets a clerk *through* a form without the mouse,
 * and this one gets them out of it. A screen where Enter walks fourteen fields and then requires a
 * click to commit has moved the mouse to the end rather than removed it.
 * </p>
 *
 * <p>
 * <strong>Alt, not Ctrl and not the function keys.</strong> Ctrl+S and Ctrl+P are the browser's Save
 * Page and Print dialog, and overriding those trains people that the shortcut they know is unsafe
 * here — badly, because the override only holds while our handler is attached. Function keys cost a
 * reach off the number pad and half of them (F1, F3, F5, F11, F12) belong to the browser anyway.
 * Alt+letter takes nothing from anybody, and Alt+P is what Tally prints with, which is the muscle
 * memory most of these shops already have.
 * </p>
 *
 * <p>
 * <strong>Matched on `event.code`, not `event.key`.</strong> Alt is a composing modifier on macOS —
 * Alt+S produces "ß" — and a non-Latin keyboard layout produces its own letter on every key. The
 * physical key is the thing being pressed in all three cases.
 * </p>
 *
 * <p>
 * <strong>Nothing destructive is bound.</strong> Delete, cancel-this-booking and lift-this-block are
 * all row actions, and a global key has no row: it would resolve to whichever one the DOM happens to
 * offer first, which is how a shortcut deletes the wrong thing. Those keep their click and their
 * confirmation.
 * </p>
 */
export type ShortcutAction = 'save' | 'print' | 'create' | 'back';

/**
 * The physical key each action answers to.
 *
 * One meaning per letter across the whole product, which is the point of declaring them here rather
 * than per screen: a clerk who learns Alt+S on the booking counter has learnt it on the stock
 * intake, the payment drawer and all eleven master forms.
 */
const SHORTCUT_CODES: Record<ShortcutAction, string> = {
  save: 'KeyS',
  print: 'KeyP',
  create: 'KeyN',
  back: 'KeyB',
};

/** What the button prints on itself, so nobody has to be told the shortcut exists. */
export function shortcutLabel(action: ShortcutAction): string {
  return `Alt+${SHORTCUT_CODES[action].replace('Key', '')}`;
}

/** The part of a keyboard event this needs — structural, so a test can hand in a plain object. */
export interface ShortcutEvent {
  readonly code: string;
  readonly altKey: boolean;
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
  readonly shiftKey: boolean;
  readonly defaultPrevented: boolean;
}

/**
 * Which action this keystroke asks for, if any.
 *
 * `ctrlKey` disqualifies rather than being ignored, and that is not tidiness: on European keyboard
 * layouts AltGr reports itself as Ctrl+Alt, so accepting it would fire Save at whoever types a `@`.
 */
export function shortcutFor(event: ShortcutEvent): ShortcutAction | null {
  if (event.defaultPrevented || !event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
    return null;
  }

  const found = Object.entries(SHORTCUT_CODES).find(([, code]) => code === event.code);

  return found ? (found[0] as ShortcutAction) : null;
}

/**
 * The button this action should press, given what is on screen.
 *
 * <p>
 * <strong>An overlay takes the whole vocabulary while it is open.</strong> A drawer over the booking
 * counter has its own Save, and Alt+S reaching past it to commit the booking underneath — a booking
 * the person cannot even see — is the worst thing a shortcut can do. So anything modal marks itself
 * `data-shortcut-layer` and the search runs inside the last one instead of the document.
 * </p>
 *
 * <p>
 * "Last" is document order, which stands in for open order because React appends: every drawer in
 * the product is rendered after the page content it covers. If two ever open from different
 * branches of the tree, this is the assumption to check first.
 * </p>
 */
export function resolveShortcutTarget(
  root: ParentNode,
  action: ShortcutAction,
): HTMLElement | null {
  const layers = root.querySelectorAll<HTMLElement>('[data-shortcut-layer]');
  const scope: ParentNode = layers[layers.length - 1] ?? root;

  const candidates = scope.querySelectorAll<HTMLElement>(`[data-shortcut="${action}"]`);

  return Array.from(candidates).find(isPressable) ?? null;
}

/**
 * Whether a marked button can actually be pressed.
 *
 * A disabled commit button is the ordinary state of an incomplete form, and clicking one does
 * nothing — so the keystroke has to be left alone rather than swallowed, or Alt+S on a form with a
 * missing field looks exactly like the shortcut being broken.
 */
function isPressable(element: HTMLElement): boolean {
  if (element.matches(':disabled') || element.getAttribute('aria-disabled') === 'true') {
    return false;
  }

  return !element.closest('[hidden], [inert], [aria-hidden="true"]');
}

/**
 * Listen for the shortcuts, for as long as the app is mounted.
 *
 * On the document rather than per button, because the whole value is that the key works while focus
 * is in the *last field* — a per-button listener only fires once the button already has focus, at
 * which point Enter would have done.
 */
export function useShortcuts(): void {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const action = shortcutFor(event);

      if (!action) {
        return;
      }

      const target = resolveShortcutTarget(document, action);

      if (!target) {
        return;
      }

      // Only once something has been found to press. Alt+P with no print button on screen must
      // still reach the browser, which is where it means something else.
      event.preventDefault();

      // Nor may it reach the control that has focus. Heard in the bubbling phase, a focused Select
      // took the "S" of Alt+S as typing and opened its list — invisible while saving closed a
      // drawer, and left hanging open over a page that stays put when a photograph fails.
      event.stopPropagation();
      target.click();
    };

    // Capture, so this runs before React's own handlers on the root and can keep the keystroke
    // from them.
    document.addEventListener('keydown', onKeyDown, true);

    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, []);
}

/**
 * Escape closes it.
 *
 * Expected of anything modal, and the alternative on these screens is the mouse. `FormDrawer` had
 * this inline and the two hand-rolled drawers had nothing at all, which is the sort of difference
 * nobody notices until they are in the one that traps them.
 */
export function useEscapeKey(onEscape: () => void, active = true): void {
  useEffect(() => {
    if (!active) {
      return;
    }

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onEscape();
      }
    };

    document.addEventListener('keydown', onKey);

    return () => document.removeEventListener('keydown', onKey);
  }, [active, onEscape]);
}
